import { S } from './state.js';
import { Audio } from './audio.js';
import { hurtPlayer } from './combat.js';
import { BAL } from './config.js';
import { rnd, rrange } from './fx.js';
import { spawnParticle } from './procs.js';
import { BOSS_KITS, bossTier, pickBossAffix } from './data/bosses.js';   // boss ability-kit registry (data-driven, no cycle: bosses.js imports only leaf modules)
export { bossTier, pickBossAffix };   // re-export so combat.js can stamp tier/affix at spawn without a second data import
// ---- Enemy helpers ----
export function enemyColor(kind) {
  return kind === 'boss' ? '#ff8a3a'
       : kind === 'shooter' ? '#c86bff'
       : kind === 'rusher' ? '#ffe14d'
       : kind === 'bomber' ? '#ff7a29'
       : kind === 'orbiter' ? '#4ad6ff'
       : kind === 'splitter' ? '#a0ff5a'
       : kind === 'brute' ? '#9aa6c8'
       : kind === 'charger' ? '#ff5edb'
       : kind === 'sniper' ? '#7c5cff'
       : kind === 'weaver' ? '#2fe0a0'
       : kind === 'summoner' ? '#c98cff'
       : kind === 'mortar' ? '#ff944d'
       : '#ff5d6e';
}
// how hard enemy damage scales with Threat — ONE curve shared by contact, bullets, mortars, bombs & hazards
export function threatDmgMul() {
  return ((1 - BAL.diff.threatDmg) + BAL.diff.threatDmg * S.threat) * BAL.diff.enemyDmg * S.dynDmg
       * (1 + (waveScale(S.waveNum) - 1) * BAL.curve.dmgShare)    // enemy damage rides 30% of the wave curve (burst ceiling still guards) × the dynamic-pressure dial
       * (S.waveNum >= BAL.curve.surgeWave ? BAL.curve.surgeDmg : 1);   // the wave-15 surge (0.19)
}
// the per-BLOW ceiling as a fraction of max HP. 0.13 froze it at 0.55 for readability ("a oneshot
// becomes two readable hits") — but with i-frames capping hit RATE and this capping hit SIZE, total
// intake stopped growing around wave 40 while %-max-HP item damage kept killing anything with any HP
// pool: runs went to wave 700 because nothing could end them (0.19, owner report). The ceiling now
// CREEPS past wave 50 — deep-void hits land up to 85%, so the mercy budget & sustain get out-paced
// eventually and every run has an end. Mercy/i-frames/armor still apply; readability holds.
export function burstCap() {
  return Math.min(BAL.player.burstCapMax, BAL.player.burstCap + Math.max(0, S.waveNum - BAL.player.burstCapDeepWave) * BAL.player.burstCapCreep);
}
// the wave curve: 1.0 through the grace waves, linear ramp to the steep wave, compounding past it
export function waveScale(n) {
  const c = BAL.curve;
  if (n <= c.grace) return 1;
  let m = 1 + (Math.min(n, c.steep) - c.grace) * c.midRamp;
  if (n > c.steep) m *= Math.pow(c.steepRamp, n - c.steep);
  return m;
}
export function enemyExplode(e) {                            // bomber's payload
  const radius = 92 + S.waveNum * 2;
  S.effects.push({ type: 'shock', x: e.x, y: e.y, r: 8, maxR: radius, life: 1, hostile: true });
  if (Math.hypot(e.x - player.x, e.y - player.y) < radius + player.r) hurtPlayer((15 + S.waveNum * 0.5) * threatDmgMul(), null, e.kind === 'bomber' ? 'bomber blast' : 'volatile blast');
  S.shake = Math.max(S.shake, 9);
  for (let k = 0; k < 18; k++) spawnParticle(e.x, e.y, 'normal');
  Audio.play('kill');
}
// boss behaviour — DATA-DRIVEN ability-kit driver (0.24 overhaul). The kits live in data/bosses.js;
// this driver runs them: HP-phase escalation, per-boss movement, then telegraph→strike with cooldowns.
// Abilities gate on minPhase (HP) AND minTier (wave depth) so deeper bosses UNLOCK more of the kit.
// Same signature as before, so the call site in combat.js is untouched.
const _bval = (x, e) => typeof x === 'function' ? x(e) : x;   // cooldown/telegraph may be number OR (e)=>number
function abilityWeight(e, a) {
  let w = a.weight || 1;
  if (e.affixB === 'echo' && (a.minPhase >= 2 || a.minTier >= 2)) w *= 2;   // Echo affix leans on the hardest moves
  return w;
}
function phaseCinematic(e) {   // beefier than the old inline flash — a readable "boss escalates" beat
  S.shake = Math.max(S.shake, 16);
  S.flash = Math.max(S.flash, S.save.settings.reduceFlash ? 0.1 : 0.28);
  S.effects.push({ type: 'shock', x: e.x, y: e.y, r: 10, maxR: e.r * 6, life: 1 });   // shockwave tell
  Audio.play('boss');
  e.gcdT = Math.max(e.gcdT, 24);   // brief beat after the transition before the next attack
}
export function bossAttack(e, dx, dy, d, mv, slow) {
  const kit = BOSS_KITS[e.btype] || BOSS_KITS.warden;
  const d1 = d || 1, ux = dx / d1, uy = dy / d1, a0 = Math.atan2(dy, dx);
  // 1) HP-phase escalation (preserve the 0.55 / 0.25 thresholds)
  const ph = e.hp < e.maxHp * 0.25 ? 2 : e.hp < e.maxHp * 0.55 ? 1 : 0;
  if (ph > e.phase) { e.phase = ph; phaseCinematic(e); }
  // 2) movement (per boss). Swift affix = quicker feet. ctx carries LIVE player aim so a telegraph can be juked.
  const ctx = { dx, dy, d: d1, ux, uy, a0, mv: e.affixB === 'swift' ? mv * 1.25 : mv, slow, px: player.x, py: player.y };
  kit.move(e, ctx);
  // 3) tick timers
  if (e.gcdT > 0) e.gcdT--;
  for (const k in e.cds) if (e.cds[k] > 0) e.cds[k]--;
  // 4) an ability is telegraphing → run it down, then strike (commit mid-telegraph; the player juked or didn't)
  if (e.actId) {
    if (--e.actT <= 0) {
      const ab = kit.abilities.find(a => a.id === e.actId);
      if (ab) { ab.strike(e, ctx); e.cds[ab.id] = _bval(ab.cooldown, e); }
      e.actId = null;
      e.gcdT = (kit.gcd || 14) * (e.affixB === 'swift' ? 0.7 : 1);
    }
    return;
  }
  // 5) idle → weight-pick the next ELIGIBLE ability (phase + tier + off-cooldown)
  if (e.gcdT > 0) return;
  const elig = [], ws = []; let tot = 0;
  for (const a of kit.abilities) {
    if (e.phase >= a.minPhase && e.tier >= a.minTier && (e.cds[a.id] || 0) <= 0) {
      const w = abilityWeight(e, a); elig.push(a); ws.push(w); tot += w;
    }
  }
  if (!tot) return;
  let x = rnd() * tot, pick = elig[0];
  for (let i = 0; i < elig.length; i++) { x -= ws[i]; if (x <= 0) { pick = elig[i]; break; } }
  e.actId = pick.id; e.actT = _bval(pick.telegraphTime, e); e.actTmax = Math.max(1, e.actT);
  e.actTele = pick.tele || 'ring'; e.actWindR = pick.windR || 0; e.actTeleAt = pick.teleAt || 'boss';   // pure render data for screens.js (no bosses.js import there)
}

// ---- Player ----
export const player = {
  x: 160, y: S.H * 0.4, vx: 6, vy: 0,
  r: 11, alive: true, trail: [],
  hp: 100, iFrames: 0, shield: 0, shieldCds: [], recentDmg: 0,   // shieldCds: one independent recharge timer per MISSING charge · recentDmg: the mercy budget's tally
  poison: 0, poisonT: 0, slowT: 0, slowAmt: 0,   // statuses inflicted by elite affixes (Venomous / Chilling)
  dashStock: 1, dashRe: 0, dashing: 0,   // dash charges, recharge timer, active-burst frames
};
S.coinsRun = 0;
S.coinsEarned = 0;                    // total coins EARNED this run (telemetry: earned − banked = spent mid-run)
S.maxCoinsRun = 0, S.gambleCount = 0;   // peak coins this run + how many Gambler's Shrine rerolls (escalates the cost)
S.deathAt = 0;                        // when the run ended — Space/Enter ignored briefly on the death screen (it's the dash key)
// ---- mythic state (0.16) ----
S.lastWeapon = -1, S.geminiCd = 0;      // Gemini Protocol: the gun you swapped AWAY from keeps fighting
S.phoenixUsed = false;                // Phoenix Clause: one refusal per run
S.chronoCharge = 0, S.chronoFreezeT = 0;   // Chronoshard: kills toward the next fracture + frames of stopped time
                                           // (a GLOBAL stop, not per-enemy freezeT — damageEnemy consumes freezeT as shatter,
                                           //  so one volley was instantly thawing the whole screen: "doesn't work")
S.mirror = null;                      // Mirror Soul: the copy entity
S.vaultSpawned = false;               // one Vault per run
S.bossRerollsRun = 0;                 // paid boss-reward rerolls this run (telemetry: which coin sink gets used)
S.cacheOpens = 0, S.cacheBuys = 0;      // item-cache engagement this run (telemetry: is the draft being used or skipped?)
S.started = false, S.dead = false, S.paused = false;
S.updateReady = false;   // a newer build is live on the server — swap in between runs (never mid-run)
S.showLeaderboard = false, S.lastScoreT = 0;   // leaderboard overlay + highlight of the just-finished run
S.showJournal = false, S.journalScroll = 0, S.journalScrollMax = 0;   // item-codex overlay (press J)
S.bloodConfirm = null;   // Blood Shrine awaiting a yes/no before the HP toll is paid
S.pandoraConfirm = null; // open Pandora's Box: { s: shrine, sel: {itemKey: bool} } — pick stacks to transmute
S.bossReward = null;     // boss-kill reward: a pick-1-of-3 draft (freezes the sim until you choose)
S.shrineDraft = null;    // open Item Cache (buy 1 of 3 — freezes the sim like the boss draft)
S.runTookDamage = false, S.bloodBoughtRun = false;   // per-run flags read by achievements
S.lastHitBy = null;                                // what landed the killing blow (telemetry: death causes)
S.lastHitDmg = 0, S.lastHitHp = 0;                   // the blow's size + your HP when it landed (death-recap forensics)
S.achToasts = [], S.journalTab = 'items';            // achievement banners + journal tab ('items'|'damage'|'achievements')
